import type { Application } from '@/api';
import { ApplicationUtil, PaymentPlanUtil } from '@/common';

export const useApplicationPolicy = (app: Ref<Application | undefined>) => {
    const { company, user, agentId, permissions } = useCompany();
    const getPolicy = (app: Application | undefined) => {
        const isSchool = company.value?.company_type === 'school';
        const isAgency = company.value?.company_type === 'agency';
        const isStudent = company.value?.company_type === 'student';
        const isStaff = company.value?.company_type === 'staff';
        const isAgencyOrStudent = isAgency || isStudent;
        const terminated = app?.status === 'withdrawn' || app?.status === 'rejected' || app?.status === 'canceled';
        const util = new ApplicationUtil(app);
        const noPaymentsMade = app?.payment_plans?.every(pp => PaymentPlanUtil.totalPaid(pp).eq(0)) ?? false;

        const isAssigned =
            app?.handlers?.some(h => h.user_id === agentId.value) ||
            permissions.value.manageStudents ||
            user.value?.id === app?.student?.user_id;

        const docEdit = isAssigned && ((isStaff && permissions.value.editApplications) || isAgencyOrStudent);
        const appEdit = isAssigned && !isStaff;

        const messages = {
            show: isAssigned,
            locked:
                (isAgency && !app?.agency_conversation_id) ||
                (isStudent && !app?.student_conversation_id) ||
                ((isStaff || isSchool) && !app?.agency_conversation_id && !app?.student_conversation_id),
        };

        const documents = {
            status: isSchool && appEdit && util.isBeforeStatus('offered'),
            upload: docEdit && util.isBeforeStatus('offered'),
            uploadOther: (isAgencyOrStudent || isSchool) && !terminated,
        };

        const steps = {
            profile: {
                edit:
                    docEdit && (app?.status === 'draft' || app?.status === 'in_review' || app?.status === 'submitted'),
                show: isAgencyOrStudent || isStaff,
                locked: false,
                finished: util.isAfterStatus('draft'),
            },
            documents: {
                edit:
                    docEdit && (app?.status === 'draft' || app?.status === 'in_review' || app?.status === 'submitted'),
                show: isAgencyOrStudent || isStaff,
                locked: false,
                finished: util.isAfterStatus('draft'),
            },
            submit: {
                edit: appEdit && app?.status === 'draft',
                show: isAgencyOrStudent || isStaff,
                locked: false,
                finished: util.isAfterStatus('in_review'),
            },
            review: {
                edit: appEdit && (app?.status === 'in_review' || app?.status === 'submitted'),
                show: isSchool || isStaff,
                locked: false,
                finished: util.isAfterStatus('in_review'),
            },
            accept: {
                edit: appEdit && app?.status === 'offered',
                show: isAgencyOrStudent || isStaff,
                locked: util.isBeforeStatus('offered'),
                signLetter: isStudent,
                finished: util.isAfterStatus('offered'),
            },
            confirm: {
                edit: appEdit && app?.status === 'accepted',
                show: isSchool || isStaff,
                locked: util.isBeforeStatus('accepted'),
                finished: util.isAfterStatus('accepted'),
            },
            finished: {
                edit: appEdit,
                show: true,
                locked: util.isBeforeStatus('confirmed') && !terminated,
                finished: util.isAfterStatus('accepted') || terminated,
            },
        };
        const payments = {
            show: true,
            locked: !util.isAfterStatus('in_review'),
            payable: util.isAfterStatus('in_review') && isStudent,
            ready: ApplicationUtil.isOfferLetterSigned(app),
        };

        const actions = {
            cancel: noPaymentsMade && !terminated && appEdit && util.isBeforeStatus('accepted'),
            sendOfferLetter:
                appEdit &&
                noPaymentsMade &&
                !util.isOfferLetterSigned() &&
                (app?.status === 'in_review' || app?.status === 'submitted' || app?.status === 'offered'),
        };

        return {
            steps,
            messages,
            payments,
            documents,
            actions,
        } as const;
    };

    const policy = computed(() => getPolicy(app.value));

    return { policy, getPolicy };
};
